import React, {
  useState,
  useCallback,
  useContext,
} from "react";

import {
  Alert,
  Box,
  Button,
  CssBaseline,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import theme from "./global-components/BookingTheme";
import { ModalHeaderComponent } from "./global-components/ModalHeader";
import RainbowAnchorContext from "./global-components/RainbowAnchor";
import { HelpOutlineRounded } from "@mui/icons-material";

// import BookingProvider from "./BookingProvider";
import BookingWizard from "./BookingWizard";
import { BookingProvider } from "./bookingContext/BookingProvider";
import { AlertProvider } from "./bookingContext/AlertContext";
import BookingStepper from "./global-components/BookingStepper";
import { BookingContext } from "./bookingContext/BookingContex";

function BookingModel({
  openModal,
  closeModal,
  selectedAptData,
  doctorDetails,
  userTimeZone,
  userDetails,
  onAuthenticationSuccess,
}) {
  // const [state, setState] = useState(initialModelStepState);

  const [showResetWarning, setShowResetWarning] = useState(false);
  // Use 'sm' breakpoint for fullScreen to match common mobile/tablet sizes
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const providerName =
  doctorDetails?.displayName ||
  [doctorDetails?.prefix, doctorDetails?.name].filter(Boolean).join(" ") ||
  doctorDetails?.doctorName ||
  "Unknown health professional";

  const BOOKING_STEPS = [
    { id: 1, title: "Contact Verification" },
    { id: 2, title: "Patient Identification" },
    { id: 3, title: "Summary & Confirm" },
  ];

  const handleClose = useCallback((event, reason) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    // Show confirmation before closing
    setShowResetWarning(true);
  }, []);

  const getSubtitleForStep = (step) => {
    switch (step) {
      case 1:
        return "Contact Verification";
      case 2:
        return "Patient Identification";
      case 3:
        return "Summary & Confirm";
      default:
        return "Book Appointment";
    }
  };

  // console.log("Data :selectedAptData : " + JSON.stringify(selectedAptData));
  // console.log("Data :doctorDetails : " + JSON.stringify(doctorDetails));
  // console.log("Data :userTimeZone : " + JSON.stringify(userTimeZone));
  // console.log("Data :userDetails : " + JSON.stringify(userDetails));
  // console.log(
  //   "Data :onAuthenticationSuccess : " + JSON.stringify(onAuthenticationSuccess)
  // );

  function BookingStepperWrapper() {
    const { step } = useContext(BookingContext);
    return <BookingStepper currentStep={step} steps={BOOKING_STEPS} />;
  }

  function BookingHeaderDynamic({ close, getSubtitle }) {
    const { step } = useContext(BookingContext);
    const subtitle = getSubtitle(step);
    return <ModalHeaderComponent close={close} subtitle={subtitle} />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Dialog
        fullScreen={fullScreen} // Full screen on mobile
        open={openModal}
        onClose={handleClose}
        aria-labelledby="booking-dialog-title"
        maxWidth="md" // Tighter max-width on desktop
        sx={{
          // Control the modal size and look for all screens
          "& .MuiPaper-root": {
            borderRadius: fullScreen ? 0 : 3, // Sharp corners on full screen
            margin: fullScreen ? 0 : "24px",
            width: "100%",
            minWidth: fullScreen ? "100%" : "maxWidth",
            height: fullScreen ? "100%" : "auto", // Take full height on mobile
            maxHeight: fullScreen ? "100%" : "95vh", // Limit height on desktop
            // padding: "10px",
            overflowY: "auto", // Ensure scrollability
            boxShadow: fullScreen ? "none" : 24,
          },
        }}
      >
        <AlertProvider>
          <BookingProvider
            selectedAptData={selectedAptData}
            doctorDetails={doctorDetails}
            userDetails={userDetails}
            userTimeZone={userTimeZone}
            onAuthenticationSuccess={onAuthenticationSuccess}
          >
            {/* Header - Reduced vertical padding (py: 1.5) to save space */}
            <DialogTitle
              id="booking-dialog-title"
              sx={{
                background:
                  "linear-gradient(to right, #0e64c7ff 0%, #4BA5EE 100%)",
                color: "#faf9f9ff",
                position: "relative",
                py: 1.5,
                px: 2,
              }}
            >
              <BookingHeaderDynamic
                close={handleClose}
                getSubtitle={getSubtitleForStep}
              />
            </DialogTitle>

            {/* Content - Reduced padding (p: 1 on xs) to save space */}
            <DialogContent dividers sx={{ p: { xs: 1, sm: 2 } }}>
              {/* Using Box instead of Container for maximum control over inner spacing */}
              <Box
                sx={{
                  pt: 1,
                  pb: 0,
                  minHeight: fullScreen ? "auto" : "50vh",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  // Ensure content is constrained on wide screens if DialogContent is wide
                  maxWidth: "md",
                  mx: "auto",
                }}
              >
                {showResetWarning && (
                  <Alert
                    severity="warning"
                    sx={{ mt: 2 }}
                    action={
                      <Stack direction="row" spacing={1}>
                        <Button
                          color="error"
                          variant="contained"
                          sx={{ px: 2 }}
                          size="small"
                          onClick={() => {
                            // resetFlow();
                            setShowResetWarning(false);
                            closeModal();
                          }}
                        >
                          Yes, Close
                        </Button>
                        <Button
                          color="inherit"
                          variant="outlined"
                          sx={{ px: 2 }}
                          size="small"
                          onClick={() => setShowResetWarning(false)}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    }
                  >
                    <Typography variant="body2">
                      Are you sure you want to close? All entered booking data
                      will be cleared.
                    </Typography>
                  </Alert>
                )}

                {/* Dynamic Stepper Component
            {flowState.step < 4 && (
              <Stepper currentStep={flowState.step} steps={STEPS} />
            )} */}

                <RainbowAnchorContext
                  doctor={providerName}
                  date={selectedAptData?.localDateDisplay}
                  time={selectedAptData?.localTimeDisplay} // Using local display time for better UX here
                  user={userDetails}
                />

                {/* Content Area - Added small vertical padding for separation */}
                <Box sx={{ flexGrow: 1, py: { xs: 2, sm: 1 } }}>
                  {/* {renderStepContent()} */}

                  <BookingStepperWrapper />
                  <BookingWizard
                    onAuthenticationSuccess={onAuthenticationSuccess}
                    closeModel={closeModal}
                  />
                </Box>

                {/* Footer - Tightened spacing and smaller font (caption) to save space */}
                <Box
                  component="footer"
                  sx={{
                    mt: 1,
                    fontSize: "0.75em",
                    color: theme.palette.grey[600],
                    pt: 1,
                    borderTop: `1px solid ${theme.palette.grey[200]}`,
                  }}
                >
                  <Typography variant="caption" display="block" sx={{ m: 0 }}>
                    Booking Time (UTC): {selectedAptData?.utcTime}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ m: 0 }}>
                    Booking Time (Local): {selectedAptData?.localDateDisplay}{" "}
                    {selectedAptData?.localTimeDisplay}{" "}
                    {userTimeZone?.userTimeZone}
                  </Typography>
                </Box>
              </Box>

              {/* Help Link - Tightened margin (mt: 1) */}
              <Box
                sx={{
                  mt: 1,
                  mb: 0,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Link
                  href="#"
                  underline="hover"
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: theme.palette.text.secondary,
                  }}
                >
                  <HelpOutlineRounded fontSize="small" sx={{ mr: 0.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    Need Help?
                  </Typography>
                </Link>
              </Box>
            </DialogContent>
          </BookingProvider>
        </AlertProvider>
      </Dialog>
    </ThemeProvider>
  );
}

export default BookingModel;
